/**
 * Tier sets (crafting.md §5): counting equipped pieces, choosing each bonus's variant and
 * validating the data against class defs. compileCharacter calls `setTransforms`.
 *
 * Variant rule, per piece count: the rework bonus is used when every id it targets exists in the
 * class def AND every feature in `needs` is in SIM_FEATURES; otherwise the `interim` bonus (built
 * on today's kit) is used. So sets are always felt, and they switch to the designed bonus by
 * themselves when the rotation rework lands.
 */
import type { ClassDef, ClassId } from '@keystone/sim';
import { TIER_SETS } from './data/tiersets';
import { SET_THRESHOLDS, TIER_SET_IDS } from './forge.constants';
import { applyTransforms, missingTargets } from './transforms';
import type { ClassTransform, EquippedGear, ItemSlot, SetBonusText, SetId, TierSetDef, TierSlot } from './types';
import { TIER_SLOTS } from './types';

export { TIER_SETS };

/**
 * Sim/contract features tier bonuses may declare in `needs`. Add a feature here when it lands
 * (e.g. 'sim:targetHasAura.minStacks' once contracts.ts has it) and the rework bonus turns on.
 */
export const SIM_FEATURES: ReadonlySet<string> = new Set<string>([
  // contracts.ts Condition targetHasAura.minStacks + combat/conditions.ts (rotation rework F7).
  'sim:targetHasAura.minStacks',
]);

export function tierSet(id: SetId): TierSetDef | undefined {
  return TIER_SETS.find((s) => s.id === id);
}

export function tierSetFor(classId: ClassId): TierSetDef | undefined {
  const id = TIER_SET_IDS[classId];
  return id ? tierSet(id) : undefined;
}

export function isTierSlot(slot: ItemSlot): slot is TierSlot {
  return (TIER_SLOTS as readonly string[]).includes(slot);
}

/** Equipped pieces of `classId`'s own tier set (only the 5 tier slots count). */
export function setPieces(gear: EquippedGear, classId: ClassId): number {
  const set = tierSetFor(classId);
  if (!set) return 0;
  let n = 0;
  for (const s of TIER_SLOTS) if (gear[s]?.setId === set.id) n++;
  return n;
}

/** Bonus thresholds reached with `pieces` (e.g. 3 → [2]). */
export function activeThresholds(pieces: number): number[] {
  return SET_THRESHOLDS.filter((t) => pieces >= t);
}

export type SetVariant = 'rework' | 'interim';

/** Which variant of a set's `pieces`-bonus applies to this class def, and why. */
export function bonusVariant(def: ClassDef, set: TierSetDef, pieces: number): { variant: SetVariant; missing: string[] } {
  const missing = [...missingTargets(def, set.bonuses[pieces] ?? []), ...(set.needs?.[pieces] ?? []).filter((f) => !SIM_FEATURES.has(f))];
  return { variant: missing.length ? 'interim' : 'rework', missing };
}

export interface SetResult {
  transforms: ClassTransform[];
  sets: { setId: SetId; pieces: number; active: number[]; variant: Record<number, SetVariant> }[];
}

/** Set-bonus transforms for a character's gear (empty below 2 pieces). */
export function setTransforms(def: ClassDef, gear: EquippedGear): SetResult {
  const set = tierSetFor(def.id);
  if (!set) return { transforms: [], sets: [] };
  const pieces = setPieces(gear, def.id);
  if (pieces === 0) return { transforms: [], sets: [] };
  const active = activeThresholds(pieces);
  const transforms: ClassTransform[] = [];
  const variant: Record<number, SetVariant> = {};
  for (const t of active) {
    const v = bonusVariant(def, set, t).variant;
    variant[t] = v;
    transforms.push(...((v === 'rework' ? set.bonuses[t] : set.interim.bonuses[t]) ?? []));
  }
  return { transforms, sets: [{ setId: set.id, pieces, active, variant }] };
}

/** Player-facing bonus rows for a class def (lit/ghost state is the UI's: compare `pieces`). */
export function setBonusRows(def: ClassDef): { pieces: number; variant: SetVariant; text: SetBonusText }[] {
  const set = tierSetFor(def.id);
  if (!set) return [];
  return SET_THRESHOLDS.map((pieces) => {
    const variant = bonusVariant(def, set, pieces).variant;
    return { pieces, variant, text: (variant === 'rework' ? set.text[pieces] : set.interim.text[pieces])! };
  });
}

/** Display name of a tier piece (e.g. "Coilguard Greathelm"), or undefined. */
export function tierPieceName(setId: SetId, slot: ItemSlot): string | undefined {
  const set = tierSet(setId);
  return set && isTierSlot(slot) ? set.pieceNames[slot] : undefined;
}

/**
 * Data checks. `interim` bonuses must ALWAYS apply to the current class defs (errors). Rework
 * bonuses report what they still wait for in `pending` (ids the rotation rework has not landed,
 * or `needs` features); once nothing is pending they must apply cleanly too.
 */
export function validateSets(classDefs: readonly ClassDef[]): { errors: string[]; pending: string[] } {
  const errors: string[] = [];
  const pending: string[] = [];
  const ids = new Set<string>();
  for (const set of TIER_SETS) {
    if (ids.has(set.id)) errors.push(`duplicate set id ${set.id}`);
    ids.add(set.id);
    if (TIER_SET_IDS[set.classId] !== set.id) errors.push(`${set.id}: TIER_SET_IDS[${set.classId}] mismatch`);
    const def = classDefs.find((c) => c.id === set.classId);
    if (!def) {
      errors.push(`${set.id}: no class ${set.classId}`);
      continue;
    }
    for (const t of SET_THRESHOLDS) {
      if (!set.bonuses[t] || !set.interim.bonuses[t] || !set.text[t] || !set.interim.text[t]) errors.push(`${set.id}: missing ${t}pc bonus or text`);
      const interim = (SET_THRESHOLDS.filter((x) => x <= t).flatMap((x) => set.interim.bonuses[x] ?? []) as ClassTransform[]);
      const miss = missingTargets(def, interim);
      if (miss.length) errors.push(`${set.id} interim ${t}pc: missing ${miss.join(', ')}`);
      else {
        try {
          applyTransforms(def, interim);
        } catch (e) {
          errors.push(`${set.id} interim ${t}pc: ${(e as Error).message}`);
        }
      }
      const v = bonusVariant(def, set, t);
      if (v.variant === 'interim') pending.push(`${set.id} ${t}pc waits for: ${v.missing.join(', ')}`);
      else {
        try {
          applyTransforms(def, set.bonuses[t] ?? []);
        } catch (e) {
          errors.push(`${set.id} ${t}pc: ${(e as Error).message}`);
        }
      }
    }
    for (const s of TIER_SLOTS) if (!set.pieceNames[s]) errors.push(`${set.id}: no ${s} name`);
  }
  return { errors, pending };
}
